import { Component } from '@theme/component';

const debug = (...args) => { if (window.__THEME_DEBUG_EVENTS__) console.log(...args); };

// Shared IntersectionObserver for lazy-init (1 observer for all carousel instances)
const pendingInit = new Set();
// Staggered init queue: process one carousel per frame to avoid layout thrashing
// when multiple carousels become visible simultaneously (e.g., collection grids).
const initQueue = [];
let initRunning = false;
function processInitQueue() {
	if (initQueue.length === 0) { initRunning = false; return; }
	const fn = initQueue.shift();
	// Never let one carousel's init failure poison the shared queue: if fn() throws and we
	// skip the rAF below, initRunning stays true forever and NO further carousel ever inits
	// (every later _lazyInit just enqueues without restarting the pump). Isolate the failure.
	try {
		fn();
	} catch (error) {
		debug('[carousel] init failed, continuing queue:', error);
	}
	requestAnimationFrame(processInitQueue);
}
// Touch devices: used to gate interaction-intent semantics — on touch, a
// pointerdown is usually just a page-scroll flick, so swipe intent is read
// from the carousel viewport's own scroll instead (see #setupNavigation).
const TOUCH_PRIMARY = window.matchMedia?.('(hover: none)').matches ?? false;

const initObserver = new IntersectionObserver((entries) => {
	for (const entry of entries) {
		if (entry.isIntersecting && pendingInit.has(entry.target)) {
			pendingInit.delete(entry.target);
			initObserver.unobserve(entry.target);
			entry.target._lazyInit();
		}
	}
	// 250% = 2.5 viewports of lookahead. Init is DOM/CPU only (clone images
	// stay gated by native lazy loading), and the staggered queue spreads the
	// work — so fully-SSR'd carousels (PDP gallery, editorial, etc.) are ready
	// well before they scroll in, even at fling speeds.
}, { rootMargin: '250% 0px' });

// Shared ResizeObserver for all carousel viewports (1 observer for all instances)
const resizeCallbacks = new Map();
const sharedResizeObserver = new ResizeObserver((entries) => {
	for (const entry of entries) {
		resizeCallbacks.get(entry.target)?.();
	}
});

/** Remove `data-shopify-*` attributes from a cloned slide and all its descendants.
 *  Clones are ghosts of the real slide — the theme editor must not discover them
 *  when the merchant selects a block in the sidebar, or the `shopify:block:select`
 *  event fires on a clone instead of the real slide and our handler can't find
 *  it in `#realSlides`. */
function stripShopifyEditorAttributes(root) {
	const strip = (el) => {
		for (const attr of Array.from(el.attributes)) {
			if (attr.name.startsWith('data-shopify')) el.removeAttribute(attr.name);
		}
	};
	strip(root);
	for (const el of root.querySelectorAll('*')) strip(el);
}

/** Compute the Shopify CDN `width=N` value for a clone image based on the real
 *  slide's rendered width. Multiplies by DPR so retina screens get crisp clones,
 *  rounds up to the nearest 100 so the CDN can cache across similar sizes, and
 *  caps at 2000 to avoid blowing up memory on ultra-wide slides. */
function computeCloneImageWidth(slideWidthPx) {
	const dpr = Math.min(window.devicePixelRatio || 1, 2);
	const target = Math.ceil((slideWidthPx * dpr) / 100) * 100;
	return Math.min(Math.max(target, 400), 2000);
}

const DRAG_CLICK_THRESHOLD = 5;       // px — movement below this is a click, not a drag
const DRAG_VELOCITY_THRESHOLD = 0.3;  // px/ms — below this, no momentum applied on release
const DRAG_MOMENTUM_DURATION = 150;   // ms — how long the release velocity "coasts"
const DRAG_VELOCITY_SAMPLES = 5;      // ring buffer size for velocity calculation
const DRAG_COMMIT_THRESHOLD = 0.08;   // fraction of slide width to commit to next slide

/**
 * @typedef {Object} CarouselSliderRefs
 * @property {HTMLElement} viewport - The viewport element
 * @property {HTMLButtonElement} [prevButton] - Previous navigation button
 * @property {HTMLButtonElement} [nextButton] - Next navigation button
 * @property {HTMLElement} [dotsContainer] - Container for dot pagination
 */

/**
 * Native CSS Scroll-Snap Carousel Web Component
 *
 * Zero-dependency carousel using CSS scroll-snap with eager clones for
 * seamless looping. No transform-based scrolling — native touch/swipe
 * physics (iOS momentum, compositor-thread acceleration).
 *
 * Architecture: eager clones + scrollend reposition
 *   loop=true init:  [C0..Cn, S0..Sn, C0..Cn]
 *                             ^-- scrollLeft here (instant, before enabling scroll)
 *   scrollend on clone → scrollLeft = realSlides[idx].offsetLeft (instant, invisible)
 *
 * @example
 * <carousel-slider
 *   data-loop="true"
 *   data-autoplay="true"
 *   data-autoplay-interval="5000"
 *   on:keydown="/handleKeydown"
 * >
 *   <div class="carousel__viewport" ref="viewport">
 *     <div class="carousel__track">
 *       <div class="carousel__slide">...</div>
 *     </div>
 *   </div>
 *   <button ref="prevButton" data-carousel-prev>Prev</button>
 *   <button ref="nextButton" data-carousel-next>Next</button>
 *   <div ref="dotsContainer" data-carousel-dots></div>
 * </carousel-slider>
 *
 * @extends {Component<CarouselSliderRefs>}
 */
class CarouselSlider extends Component {
	/** @type {HTMLElement|null} */
	#track = null;

	/** @type {HTMLElement[]} */
	#realSlides = [];

	/** @type {HTMLElement[]} */
	#allSlides = [];

	/** @type {number} */
	#currentIndex = 0;

	/** @type {boolean} */
	#isLoop = false;

	/** @type {'adaptive'|'full'|'low'} */
	#cloneQuality = 'adaptive';

	/** @type {boolean} */
	#isRepositioning = false;

	/** @type {number|null} */
	#autoplayTimer = null;

	/** @type {boolean} */
	#autoplayStopped = false;

	/** @type {boolean} */
	#reducedMotion = false;

	/** @type {AbortController|null} */
	#abort = null;


	/** @type {number} */
	#scrollDebounceTimer = 0;

	/** @type {number} */
	#lastDotIndex = -1;

	/** @type {number} */
	#clonesPerSide = 0;

	/** @type {number} */
	#repositionTimeout = 0;

	/** @type {number} */
	#resizeDebounceTimer = 0;

	/** @type {boolean} */
	#isResizing = false;

	/** @type {boolean} */
	#isInitializing = false;

	/** @type {boolean} */
	#isAnimating = false;

	/** @type {number} Current animation frame ID for cancellation */
	#animationFrame = 0;

	/** @type {boolean} */
	#isDragging = false;

	/** @type {number} */
	#dragStartX = 0;

	/** @type {number} */
	#dragStartScrollLeft = 0;

	/** @type {number} */
	#dragDelta = 0;

	/** @type {Array<{x: number, t: number}>} */
	#dragVelocityTracker = [];

	/** @type {boolean} */
	#dragClickPrevented = false;

	/** @type {boolean} */
	#expandedClones = false;

	/** @type {number|null} */
	#cloneSetsOverride = null;

	/** @type {number[]} Cached offsetLeft for each slide in #allSlides order */
	#slidePositions = [];

	/** @type {HTMLElement[]} Cached dot button references */
	#dotElements = [];

	/** @type {number} Cached viewport.clientWidth, updated on resize */
	#viewportWidth = 0;

	/** @type {boolean} */
	initialized = false;

	connectedCallback() {
		super.connectedCallback();
		pendingInit.add(this);
		initObserver.observe(this);

		if (window.Shopify?.designMode) {
			this.addEventListener('shopify:block:select', this.#handleBlockSelect);
		}
	}

	disconnectedCallback() {
		pendingInit.delete(this);
		initObserver.unobserve(this);
		if (window.Shopify?.designMode) {
			this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
		}
		this.destroy();
		super.disconnectedCallback();
	}

	/**
	 * Called after a Section Rendering API morph replaces the carousel's
	 * children (e.g. collection filter apply, variant change). The server
	 * renders only real slides — all of our JS-generated clones are gone,
	 * and internal caches (#realSlides, #slidePositions, #currentIndex,
	 * scroll position) point at stale or detached DOM. Tear down and
	 * re-queue for lazy init so the next intersection rebuilds everything
	 * from the fresh DOM.
	 */
	updatedCallback() {
		super.updatedCallback();
		if (!this.initialized) return;
		this.destroy();
		pendingInit.add(this);
		initObserver.observe(this);
	}

	/**
	 * Re-initialize synchronously from the current slide DOM. Used after extra slides
	 * are hydrated into the track at runtime — e.g. a product card's deferred gallery
	 * slides revealed on first hover/touch. Unlike updatedCallback's lazy re-queue, this
	 * rebuilds immediately so the caller can scroll/preview right away. Safe to call when
	 * the carousel was never initialized (a single-slide gallery bails out of #init).
	 */
	rehydrate() {
		if (this.initialized) this.destroy();
		pendingInit.delete(this);
		initObserver.unobserve(this);
		this.#init();
	}

	/** Theme editor: scroll the selected slide into view when a merchant
	 *  picks a carousel slide (or a block nested inside one) in the sidebar. */
	#handleBlockSelect = (event) => {
		const slide = event.target?.closest?.('.carousel__slide');
		if (!slide) return;
		// Ignore slides that belong to a nested carousel
		if (slide.closest('carousel-slider') !== this) return;

		if (!this.initialized) {
			pendingInit.delete(this);
			initObserver.unobserve(this);
			this.#init();
		}

		const realIndex = this.#realSlides.indexOf(slide);
		if (realIndex === -1) return;

		this.#scrollToSlide(realIndex, true);
	};

	/** Called by the shared IntersectionObserver when this element enters the viewport.
	 *  Staggered: one carousel initializes per animation frame to avoid batching
	 *  multiple clone setups (with forced reflows) into a single long task. */
	_lazyInit() {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				initQueue.push(() => this.#init());
				if (!initRunning) {
					initRunning = true;
					processInitQueue();
				}
			});
		});
	}

	#init() {
		if (this.initialized) return;

		const { viewport } = this.refs;
		if (!viewport) return;

		this.#track = viewport.querySelector('.carousel__track');
		if (!this.#track) return;

		// Clean up stale clones from bfcache or IndexedDB-restored DOM
		const staleClones = this.#track.querySelectorAll('[data-clone]');
		if (staleClones.length > 0) {
			debug('[carousel] cleaning stale clones:', staleClones.length);
			for (const clone of staleClones) {
				clone.remove();
			}
		}

		this.#realSlides = [...this.#track.querySelectorAll('.carousel__slide')];
		if (this.#realSlides.length === 0) return;

		// Single slide: just preload and bail
		if (this.#realSlides.length < 2) {
			debug('[carousel] single slide, skipping init');
			this.#preloadVisibleSlides();
			return;
		}

		this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		this.#isLoop = this.dataset.loop !== 'false';
		this.#cloneQuality = this.dataset.cloneQuality || 'adaptive';
		this.#abort = new AbortController();
		this.#viewportWidth = viewport.clientWidth;
		this.#isInitializing = true;

		if (window.__THEME_DEBUG_EVENTS__) {
			debug('[carousel] init', {
				realSlides: this.#realSlides.length,
				loop: this.#isLoop,
				reducedMotion: this.#reducedMotion,
				viewportWidth: viewport.clientWidth,
				scrollWidth: viewport.scrollWidth,
				dataset: { ...this.dataset },
			});
		}

		if (this.#isLoop) {
			this.#setupClones();
		} else {
			this.#allSlides = [...this.#realSlides];
			viewport.classList.add('carousel__viewport--ready');
			this.#cacheSlidePositions();
		}

		this.#setupScrollTracking();
		this.#setupNavigation();
		this.#setupDots();
		this.#setupDrag();
		this.#setupAutoplay();
		this.#setupResize();
		this.setupSectionUnload();

		this.#preloadVisibleSlides();
		this.#updateNavigationState();
		this.#updateDots();

		this.initialized = true;
		// Clear initializing flag after scrollend from setupClones has fired
		requestAnimationFrame(() => { this.#isInitializing = false; });
		debug('[carousel] init complete');
	}

	#setupClones() {
		const { viewport } = this.refs;
		const realCount = this.#realSlides.length;

		// Calculate clone sets: 1 viewport of buffer per side is enough for
		// scroll-snap repositioning. More sets only needed when many slides are visible.
		const slideWidth = this.#realSlides[0].offsetWidth;
		// Guard: if slides have no width (content-visibility: auto, offscreen, or mid-resize),
		// skip cloning entirely — will be recalculated on next rebuildClones.
		if (!slideWidth || slideWidth < 10) {
			this.#allSlides = [...this.#realSlides];
			viewport.classList.add('carousel__viewport--ready');
			return;
		}
		const visibleCount = Math.max(1, Math.ceil(viewport.clientWidth / slideWidth));
		const cloneSets = this.#cloneSetsOverride ?? Math.max(1, Math.min(Math.ceil((visibleCount * 2) / realCount), 3));

		// Batch all layout reads upfront to avoid forced reflows during clone loop.
		// Dimensions don't change between clones of the same slide.
		const slideDimensions = [];
		for (let i = 0; i < realCount; i++) {
			slideDimensions.push({
				w: this.#realSlides[i].offsetWidth,
				h: this.#realSlides[i].offsetHeight,
			});
		}

		const before = document.createDocumentFragment();
		const after = document.createDocumentFragment();

		for (let set = 0; set < cloneSets; set++) {
			for (let i = 0; i < realCount; i++) {
				const cloneBefore = this.#realSlides[i].cloneNode(true);
				const cloneAfter = this.#realSlides[i].cloneNode(true);

				const { w, h } = slideDimensions[i];
				for (const clone of [cloneBefore, cloneAfter]) {
					clone.setAttribute('data-clone', '');
					clone.setAttribute('aria-hidden', 'true');
					clone.setAttribute('data-real-index', String(i));
					clone.removeAttribute('id');
					stripShopifyEditorAttributes(clone);
					for (const el of clone.querySelectorAll('[tabindex], a, button, input, select, textarea')) {
						el.setAttribute('tabindex', '-1');
					}
					this.#processCloneImages(clone, w);
					// Explicit size + visible — no content-visibility recalculations
					// Uses cached dimensions to avoid forced reflow per clone.
					clone.style.contentVisibility = 'visible';
					clone.style.containIntrinsicSize = `${w}px ${h}px`;
				}

				before.appendChild(cloneBefore);
				after.appendChild(cloneAfter);
			}
		}

		this.#track.prepend(before);
		this.#track.append(after);

		this.#allSlides = [...this.#track.querySelectorAll('.carousel__slide')];

		// Keep real slides always rendered — prevents GPU texture eviction
		// that causes a flash when repositioning from clones back to real slides.
		// Clones keep content-visibility: auto (we never jump TO them).
		for (const slide of this.#realSlides) {
			slide.style.contentVisibility = 'visible';
		}

		// Position at first real slide (instant, before enabling scroll-snap).
		// Calculate from cached dimensions to avoid forced reflow after DOM append.
		const totalBeforeWidth = cloneSets * slideDimensions.reduce((sum, d) => sum + d.w, 0);
		viewport.scrollLeft = totalBeforeWidth;

		this.#clonesPerSide = cloneSets * realCount;
		if (window.__THEME_DEBUG_EVENTS__) {
			debug('[carousel] setupClones', {
				realCount,
				cloneSets,
				clonesPerSide: this.#clonesPerSide,
				totalInDOM: this.#allSlides.length,
				initialScrollLeft: totalBeforeWidth,
			});
		}

		// Now enable scroll-snap + overflow
		viewport.classList.add('carousel__viewport--ready');
		this.#cacheSlidePositions();
	}

	/**
	 * Cache offsetLeft for every slide in one batched read.
	 * Eliminates forced reflow in scroll/animation hot paths.
	 * Must be called after any DOM mutation that changes slide positions.
	 */
	#cacheSlidePositions() {
		const slides = this.#allSlides;
		const positions = new Array(slides.length);
		for (let i = 0; i < slides.length; i++) {
			positions[i] = slides[i].offsetLeft;
		}
		this.#slidePositions = positions;
		this.#viewportWidth = this.refs.viewport?.clientWidth || this.#viewportWidth;
	}

	/**
	 * Get cached position for a real slide by its real index.
	 * Falls back to live offsetLeft if cache is empty (safety net).
	 */
	#getRealSlidePosition(realIndex) {
		const domIndex = this.#clonesPerSide + realIndex;
		return this.#slidePositions[domIndex] ?? this.#realSlides[realIndex]?.offsetLeft ?? 0;
	}

	#setupScrollTracking() {
		const { viewport } = this.refs;
		const signal = this.#abort?.signal;
		const hasNativeScrollend = 'onscrollend' in window;

		const handleScrollend = () => {
			if (this.#isDragging) return;
			if (this.#isAnimating) return;
			if (this.#isResizing) return;
			if (this.#isInitializing) return;
			if (this.#isRepositioning) {
				debug('[carousel] scrollend SKIPPED (repositioning in progress)');
				return;
			}

			const domIdx = this.#getClosestSlideIndex();
			const realIdx = this.#getRealIndex(domIdx);
			const slide = this.#allSlides[domIdx];
			const isClone = slide?.hasAttribute('data-clone');

			debug('[carousel] scrollend', {
				domIdx,
				realIdx,
				isClone,
				scrollLeft: viewport.scrollLeft,
				slideOffsetLeft: slide?.offsetLeft,
				slideDataRealIndex: slide?.dataset.realIndex,
				totalSlides: this.#allSlides.length,
				prevCurrentIndex: this.#currentIndex,
			});

			this.#currentIndex = realIdx;
			this.#updateNavigationState();
			this.#updateDots();
			this.#preloadAdjacentSlides();

			// Expand clone buffer after first real interaction for smoother fast swiping.
			// Appends extra clones without rebuilding — preserves scroll position.
			if (this.#isLoop && !this.#expandedClones && this.hasAttribute('data-interacted')) {
				this.#expandedClones = true;
				this.#expandClones();
			}

			// If we landed on a clone, reposition immediately to the real slide.
			// Real slides have content-visibility: visible so their GPU textures
			// are always ready — the jump is invisible (identical pixels).
			if (this.#isLoop && isClone) {
				debug('[carousel] landed on clone → repositioning to real slide', realIdx);
				this.#repositionToReal(realIdx);
			}
		};

		if (hasNativeScrollend) {
			viewport.addEventListener('scrollend', handleScrollend, { signal });
		} else {
			// Safari <17.4 polyfill: debounce scroll events
			viewport.addEventListener('scroll', () => {
				clearTimeout(this.#scrollDebounceTimer);
				this.#scrollDebounceTimer = setTimeout(handleScrollend, 100);
			}, { signal, passive: true });
		}

		// Combined scroll handler: dot updates + boundary safety net
		viewport.addEventListener('scroll', () => {
			if (this.#isRepositioning || this.#isResizing) return;

			// Interaction tracking. Desktop: safety-net fallback (pointerdown/
			// keydown usually fired earlier). Touch: this IS the primary swipe
			// trigger — the viewport only scrolls when the user actually swipes,
			// so gallery images restore here, never on page-scroll touches.
			if (!this.#isInitializing) this.#markInteracted();

			// Dot update (uses cached positions — no reflow)
			const domIdx = this.#getClosestSlideIndex();
			const realIdx = this.#getRealIndex(domIdx);
			if (realIdx !== this.#lastDotIndex) {
				this.#lastDotIndex = realIdx;
				this.#updateDotsToIndex(realIdx);
			}

			// Boundary safety net (loop mode only — uses cached values)
			if (this.#isLoop && !this.#isDragging && !this.#isAnimating && !this.#isResizing && !this.#isInitializing) {
				const scrollLeft = viewport.scrollLeft;
				const positions = this.#slidePositions;
				const slideWidth = positions.length > 1 ? positions[1] - positions[0] : this.#viewportWidth;
				const buffer = slideWidth * 2;
				const lastPos = positions[positions.length - 1] || 0;
				const maxScroll = lastPos + slideWidth - this.#viewportWidth;

				if (scrollLeft < buffer) {
					debug('[carousel] SAFETY NET — near start, expanding clones before');
					this.#expandClonesInDirection('before');
				} else if (scrollLeft > maxScroll - buffer) {
					debug('[carousel] SAFETY NET — near end, expanding clones after');
					this.#expandClonesInDirection('after');
				}
			}
		}, { signal, passive: true });

	}


	/**
	 * Animate scroll to a target position with cubic ease-out.
	 * Shared by arrow navigation and drag settlement for consistent feel.
	 * @param {number} targetLeft - Target scrollLeft value
	 * @param {number} [duration=280] - Animation duration in ms
	 */
	#animateToSlide(targetLeft, duration = 380) {
		const { viewport } = this.refs;

		// Cancel any in-flight animation to prevent concurrent RAF loops
		cancelAnimationFrame(this.#animationFrame);

		if (this.#reducedMotion) {
			viewport.scrollLeft = targetLeft;
			this.#isAnimating = false;
			viewport.style.scrollSnapType = '';
			return;
		}

		const startLeft = viewport.scrollLeft;
		const distance = targetLeft - startLeft;
		if (Math.abs(distance) < 1) {
			viewport.scrollLeft = targetLeft;
			this.#isAnimating = false;
			viewport.style.scrollSnapType = '';
			return;
		}

		this.#isAnimating = true;
		viewport.style.scrollSnapType = 'none';
		const easeOut = (t) => 1 - Math.pow(1 - t, 2);
		const startTime = performance.now();

		const animate = (now) => {
			const elapsed = now - startTime;
			const progress = Math.min(elapsed / duration, 1);
			viewport.scrollLeft = startLeft + distance * easeOut(progress);
			if (progress < 1) {
				this.#animationFrame = requestAnimationFrame(animate);
			} else {
				viewport.scrollLeft = targetLeft;
				this.#isAnimating = false;
				viewport.style.scrollSnapType = '';
			}
		};
		this.#animationFrame = requestAnimationFrame(animate);
	}

	#scrollNextInternal() {
		const domIdx = this.#getClosestSlideIndex();

		if (this.#isLoop) {
			let targetIdx = domIdx + 1;
			// Expand clones if at the end of the track
			if (targetIdx >= this.#allSlides.length) {
				this.#expandClonesInDirection('after');
				targetIdx = domIdx + 1;
			}
			debug('[carousel] scrollNext (loop)', {
				currentDomIdx: domIdx,
				targetDomIdx: targetIdx,
				targetOffsetLeft: this.#slidePositions[targetIdx],
				isTargetClone: this.#allSlides[targetIdx]?.hasAttribute('data-clone'),
			});
			if (targetIdx < this.#allSlides.length) {
				this.#animateToSlide(this.#slidePositions[targetIdx]);
			}
		} else {
			const nextReal = Math.min(this.#currentIndex + 1, this.#realSlides.length - 1);
			debug('[carousel] scrollNext (no-loop)', { currentIndex: this.#currentIndex, nextReal });
			this.#scrollToSlide(nextReal);
		}
	}

	#scrollPrevInternal() {
		const domIdx = this.#getClosestSlideIndex();

		if (this.#isLoop) {
			let targetIdx = domIdx - 1;
			// Expand clones if at the start of the track
			if (targetIdx < 0) {
				this.#expandClonesInDirection('before');
				// After prepending, all indices shifted — recalculate
				targetIdx = this.#getClosestSlideIndex() - 1;
			}
			debug('[carousel] scrollPrev (loop)', {
				currentDomIdx: domIdx,
				targetDomIdx: targetIdx,
				targetOffsetLeft: this.#slidePositions[targetIdx],
				isTargetClone: this.#allSlides[targetIdx]?.hasAttribute('data-clone'),
			});
			if (targetIdx >= 0) {
				this.#animateToSlide(this.#slidePositions[targetIdx]);
			}
		} else {
			const prevReal = Math.max(this.#currentIndex - 1, 0);
			debug('[carousel] scrollPrev (no-loop)', { currentIndex: this.#currentIndex, prevReal });
			this.#scrollToSlide(prevReal);
		}
	}

	#scrollToSlide(realIndex, instant = false) {
		const pos = this.#getRealSlidePosition(realIndex);
		if (instant || this.#reducedMotion) {
			this.refs.viewport.scrollLeft = pos;
			return;
		}
		this.#animateToSlide(pos);
	}

	#repositionToReal(realIndex) {
		const { viewport } = this.refs;
		const slide = this.#realSlides[realIndex];
		if (!slide) return;
		this.#isRepositioning = true;

		const beforeScroll = viewport.scrollLeft;
		const targetOffset = this.#getRealSlidePosition(realIndex);

		// Kill native scroll momentum by briefly hiding overflow.
		// Without this, trackpad inertia continues after the teleport,
		// pushing back into clone territory and causing a reposition loop.
		viewport.style.overflow = 'hidden';
		viewport.style.scrollSnapType = 'none';
		viewport.scrollLeft = targetOffset;

		debug('[carousel] repositionToReal', {
			realIndex,
			beforeScrollLeft: beforeScroll,
			targetOffsetLeft: targetOffset,
			afterScrollLeft: viewport.scrollLeft,
			jumpDistance: Math.abs(viewport.scrollLeft - beforeScroll),
		});

		clearTimeout(this.#repositionTimeout);
		this.#repositionTimeout = setTimeout(() => {
			if (this.#isRepositioning) {
				viewport.style.overflow = '';
				viewport.style.scrollSnapType = '';
				this.#isRepositioning = false;
				debug('[carousel] reposition timeout — force-cleared');
			}
		}, 200);

		requestAnimationFrame(() => {
			// Restore overflow first frame — allows scrolling again
			viewport.style.overflow = '';
			requestAnimationFrame(() => {
				// Restore snap second frame — after browser has painted
				clearTimeout(this.#repositionTimeout);
				viewport.style.scrollSnapType = '';
				this.#isRepositioning = false;
				debug('[carousel] reposition complete, scrollLeft:', viewport.scrollLeft);
			});
		});
	}

	#getClosestSlideIndex() {
		const { viewport } = this.refs;
		return this.#getClosestSlideIndexAt(viewport.scrollLeft);
	}

	#getClosestSlideIndexAt(scrollLeft) {
		const positions = this.#slidePositions;
		if (positions.length === 0) return 0;

		// Binary search — positions are sorted (flex LTR layout)
		let lo = 0, hi = positions.length - 1;
		while (lo < hi) {
			const mid = (lo + hi) >> 1;
			if (positions[mid] < scrollLeft) lo = mid + 1;
			else hi = mid;
		}
		// Check lo and lo-1 for closest
		if (lo > 0 && Math.abs(positions[lo - 1] - scrollLeft) <= Math.abs(positions[lo] - scrollLeft)) {
			return lo - 1;
		}
		return lo;
	}

	#getRealIndex(domIndex) {
		const slide = this.#allSlides[domIndex];
		if (!slide) return 0;
		if (slide.hasAttribute('data-clone')) {
			return parseInt(slide.dataset.realIndex, 10);
		}
		return this.#realSlides.indexOf(slide);
	}

	#setupNavigation() {
		const { prevButton, nextButton, viewport } = this.refs;
		const signal = this.#abort?.signal;

		// Upgrade lazy images to eager as soon as the user signals intent —
		// pointerdown fires ~100-300ms before scroll, giving fetches a head
		// start so adjacent slides aren't still loading when they swipe in.
		// TOUCH: viewport pointerdown is NOT intent — every page-scroll flick
		// starts with a pointerdown on some card, and treating it as intent
		// loaded that card's whole gallery (+ a PDP prerender) on every flick,
		// competing with the first images that keep the grid stable. A real
		// swipe scrolls the carousel viewport itself — the scroll handler in
		// #setupScrollTracking fires #markInteracted there instead.
		const onIntent = () => this.#markInteracted();
		if (viewport && !TOUCH_PRIMARY) {
			viewport.addEventListener('pointerdown', onIntent, { signal, passive: true });
		}
		if (prevButton) {
			prevButton.addEventListener('pointerdown', onIntent, { signal, passive: true });
			prevButton.addEventListener('click', () => this.#scrollPrevInternal(), { signal });
		}
		if (nextButton) {
			nextButton.addEventListener('pointerdown', onIntent, { signal, passive: true });
			nextButton.addEventListener('click', () => this.#scrollNextInternal(), { signal });
		}
	}

	#markInteracted() {
		if (this.hasAttribute('data-interacted')) return;
		this.dataset.interacted = '';
		this.#restoreDeferredSrc(this);
		for (const img of this.querySelectorAll('img[loading="lazy"]')) {
			img.loading = 'eager';
		}
		const card = this.closest('product-card');
		const url = card?.dataset.productUrl;
		if (url && HTMLScriptElement.supports?.('speculationrules')) {
			const s = document.createElement('script');
			s.type = 'speculationrules';
			s.textContent = JSON.stringify({ prerender: [{ source: 'list', urls: [url] }] });
			document.head.appendChild(s);
		}
	}

	#updateNavigationState() {
		const { prevButton, nextButton, viewport } = this.refs;

		// hasOverflow = the carousel actually needs scrolling. If all slides
		// fit inside the viewport (e.g. 1 slide with slides-per-view:4), arrows
		// are hidden entirely — there's nothing to navigate to.
		const hasOverflow = viewport ? viewport.scrollWidth > viewport.clientWidth + 1 : false;

		// Use actual scroll position (not currentIndex) so the buttons respect
		// slides-per-view: next is disabled when the viewport is already at
		// max scrollLeft, even if several slides are visible past currentIndex.
		// Loop mode keeps both arrows enabled — you can always wrap around.
		let canPrev, canNext;
		if (this.#isLoop || !viewport) {
			canPrev = hasOverflow;
			canNext = hasOverflow;
		} else {
			const maxScroll = viewport.scrollWidth - viewport.clientWidth;
			const scrollLeft = viewport.scrollLeft;
			canPrev = hasOverflow && scrollLeft > 1;
			canNext = hasOverflow && scrollLeft < maxScroll - 1;
		}

		if (prevButton) {
			prevButton.disabled = !canPrev;
			prevButton.setAttribute('aria-disabled', String(!canPrev));
			prevButton.hidden = !hasOverflow;
		}
		if (nextButton) {
			nextButton.disabled = !canNext;
			nextButton.setAttribute('aria-disabled', String(!canNext));
			nextButton.hidden = !hasOverflow;
		}
	}

	#setupDots() {
		const { dotsContainer } = this.refs;
		if (!dotsContainer) return;

		dotsContainer.innerHTML = '';
		const signal = this.#abort?.signal;
		const template = this.dataset.slideLabelTemplate || 'Go to slide [index]';

		const dots = [];
		for (let i = 0; i < this.#realSlides.length; i++) {
			const dot = document.createElement('button');
			dot.type = 'button';
			dot.className = 'carousel__dot';
			dot.setAttribute('role', 'tab');
			dot.setAttribute('aria-label', template.replace('[index]', String(i + 1)));
			const idx = i;
			dot.addEventListener('click', () => this.#scrollToSlide(idx), { signal });
			dotsContainer.appendChild(dot);
			dots.push(dot);
		}
		this.#dotElements = dots;
	}

	#updateDots() {
		this.#updateDotsToIndex(this.#currentIndex);
	}

	#updateDotsToIndex(index) {
		const dots = this.#dotElements;
		for (let i = 0; i < dots.length; i++) {
			const selected = i === index;
			dots[i].classList.toggle('carousel__dot--selected', selected);
			dots[i].setAttribute('aria-selected', selected ? 'true' : 'false');
		}
	}

	/**
	 * Handle keyboard navigation — bound via on:keydown="/handleKeydown"
	 * @param {KeyboardEvent} event
	 */
	handleKeydown(event) {
		switch (event.key) {
			case 'ArrowLeft':
				event.preventDefault();
				this.#markInteracted();
				this.#scrollPrevInternal();
				break;
			case 'ArrowRight':
				event.preventDefault();
				this.#markInteracted();
				this.#scrollNextInternal();
				break;
			case 'Home':
				event.preventDefault();
				this.#markInteracted();
				this.#scrollToSlide(0);
				break;
			case 'End':
				event.preventDefault();
				this.#markInteracted();
				this.#scrollToSlide(this.#realSlides.length - 1);
				break;
		}
	}

	#setupDrag() {
		const { viewport } = this.refs;
		const signal = this.#abort?.signal;
		viewport.addEventListener('pointerdown', this.#onDragStart, { signal });
		viewport.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
		this.addEventListener('click', this.#onClickAfterDrag, { signal, capture: true });
	}

	#onDragStart = (event) => {
		if (event.pointerType !== 'mouse') return;
		if (event.button !== 0) return;
		if (this.#isRepositioning) return;
		if (event.target.closest('button, [data-carousel-prev], [data-carousel-next], [data-carousel-dots]')) return;

		const { viewport } = this.refs;
		this.#dragStartX = event.clientX;
		this.#dragStartScrollLeft = viewport.scrollLeft;
		this.#dragDelta = 0;
		this.#dragClickPrevented = false;
		this.#dragVelocityTracker = [{ x: viewport.scrollLeft, t: performance.now() }];

		const signal = this.#abort?.signal;
		viewport.addEventListener('pointermove', this.#onDragMove, { signal });
		viewport.addEventListener('pointerup', this.#onDragEnd, { signal });
		viewport.addEventListener('pointercancel', this.#onDragEnd, { signal });
	};

	#onDragMove = (event) => {
		const { viewport } = this.refs;
		const deltaX = event.clientX - this.#dragStartX;
		this.#dragDelta = Math.abs(deltaX);

		if (!this.#isDragging && this.#dragDelta > DRAG_CLICK_THRESHOLD) {
			this.#isDragging = true;
			this.#dragClickPrevented = true;
			viewport.setPointerCapture(event.pointerId);
			viewport.style.scrollSnapType = 'none';
			viewport.classList.add('carousel__viewport--dragging');
			viewport.addEventListener('lostpointercapture', this.#onDragEnd);
		}

		if (!this.#isDragging) return;

		viewport.scrollLeft = this.#dragStartScrollLeft - deltaX;

		const now = performance.now();
		this.#dragVelocityTracker.push({ x: viewport.scrollLeft, t: now });
		if (this.#dragVelocityTracker.length > DRAG_VELOCITY_SAMPLES) {
			this.#dragVelocityTracker.shift();
		}
	};

	#onDragEnd = (event) => {
		const { viewport } = this.refs;
		viewport.removeEventListener('pointermove', this.#onDragMove);
		viewport.removeEventListener('pointerup', this.#onDragEnd);
		viewport.removeEventListener('pointercancel', this.#onDragEnd);
		viewport.removeEventListener('lostpointercapture', this.#onDragEnd);

		if (!this.#isDragging) return;
		this.#isDragging = false;

		if (viewport.hasPointerCapture(event.pointerId)) {
			viewport.releasePointerCapture(event.pointerId);
		}

		viewport.classList.remove('carousel__viewport--dragging');

		const velocity = this.#calculateDragVelocity();
		this.#dragVelocityTracker = [];

		if (this.#reducedMotion) {
			viewport.style.scrollSnapType = '';
			return;
		}

		// Momentum: exponential deceleration (iOS-like physics).
		// When velocity exhausts, settle smoothly to nearest slide via #animateToSlide.
		const FRICTION = 0.92;
		const MIN_VELOCITY = 0.3;
		let v = -velocity * 0.8;

		const animate = () => {
			v *= FRICTION;
			if (Math.abs(v) < MIN_VELOCITY) {
				const domIdx = this.#getClosestSlideIndex();
				this.#animateToSlide(this.#slidePositions[domIdx], 200);
				return;
			}
			viewport.scrollLeft += v;
			requestAnimationFrame(animate);
		};

		const scrollDelta = viewport.scrollLeft - this.#dragStartScrollLeft;
		const slideWidth = (this.#slidePositions.length > 1 ? this.#slidePositions[1] - this.#slidePositions[0] : this.#viewportWidth) || 1;
		const pastCommit = Math.abs(scrollDelta) > slideWidth * DRAG_COMMIT_THRESHOLD;

		if (Math.abs(velocity) > DRAG_VELOCITY_THRESHOLD) {
			// If user dragged past commit threshold, guarantee advancing
			// even if momentum alone wouldn't cross the midpoint.
			if (pastCommit) {
				const direction = scrollDelta > 0 ? 1 : -1;
				const startIdx = this.#getClosestSlideIndexAt(this.#dragStartScrollLeft);
				const targetIdx = Math.max(0, Math.min(startIdx + direction, this.#allSlides.length - 1));
				this.#animateToSlide(this.#slidePositions[targetIdx]);
			} else {
				this.#isAnimating = true;
				requestAnimationFrame(animate);
			}
		} else {
			// No momentum — settle to target slide
			let targetIdx;

			if (pastCommit) {
				const direction = scrollDelta > 0 ? 1 : -1;
				const startIdx = this.#getClosestSlideIndexAt(this.#dragStartScrollLeft);
				targetIdx = Math.max(0, Math.min(startIdx + direction, this.#allSlides.length - 1));
			} else {
				targetIdx = this.#getClosestSlideIndexAt(this.#dragStartScrollLeft);
			}

			this.#animateToSlide(this.#slidePositions[targetIdx]);
		}
	};

	#calculateDragVelocity() {
		const tracker = this.#dragVelocityTracker;
		if (tracker.length < 2) return 0;
		const first = tracker[0];
		const last = tracker[tracker.length - 1];
		const dt = last.t - first.t;
		if (dt === 0) return 0;
		return (last.x - first.x) / dt;
	}

	#onClickAfterDrag = (event) => {
		if (!this.#dragClickPrevented) return;
		event.preventDefault();
		event.stopPropagation();
		this.#dragClickPrevented = false;
	};

	#setupAutoplay() {
		const autoplayAttr = this.dataset.autoplay;
		if (autoplayAttr !== 'true' || this.#reducedMotion) return;

		const signal = this.#abort?.signal;
		const interval = parseInt(this.dataset.autoplayInterval || this.dataset.interval, 10) || 5000;

		this.#startAutoplay(interval);

		if (this.dataset.pauseOnHover !== 'false') {
			this.addEventListener('mouseenter', () => this.#clearAutoplay(), { signal });
			this.addEventListener('mouseleave', () => {
				if (!this.#autoplayStopped) this.#startAutoplay(interval);
			}, { signal });
		}

		this.addEventListener('focusin', () => this.#clearAutoplay(), { signal });
		this.addEventListener('focusout', () => {
			if (!this.#autoplayStopped) this.#startAutoplay(interval);
		}, { signal });

		if (this.dataset.pauseOnInteraction !== 'false') {
			this.addEventListener('pointerdown', () => {
				this.#autoplayStopped = true;
				this.#clearAutoplay();
			}, { signal });
		}
	}

	#startAutoplay(interval) {
		this.#clearAutoplay();
		this.#autoplayTimer = setInterval(() => this.#scrollNextInternal(), interval);
	}

	#clearAutoplay() {
		if (this.#autoplayTimer !== null) {
			clearInterval(this.#autoplayTimer);
			this.#autoplayTimer = null;
		}
	}

	#expandClones() {
		const { viewport } = this.refs;
		const realCount = this.#realSlides.length;
		const existingSets = Math.round(this.#clonesPerSide / realCount);
		const targetSets = 3;
		const setsToAdd = targetSets - existingSets;
		if (setsToAdd <= 0) return;

		// Batch all dimension reads upfront to avoid forced reflows in clone loop
		const slideDimensions = [];
		for (let i = 0; i < realCount; i++) {
			slideDimensions.push({
				w: this.#realSlides[i].offsetWidth,
				h: this.#realSlides[i].offsetHeight,
			});
		}

		const before = document.createDocumentFragment();
		const after = document.createDocumentFragment();

		for (let set = 0; set < setsToAdd; set++) {
			for (let i = 0; i < realCount; i++) {
				const { w, h } = slideDimensions[i];
				before.appendChild(this.#createClone(i, w, h));
				after.appendChild(this.#createClone(i, w, h));
			}
		}

		// Save scroll position, prepend/append, restore position
		const scrollBefore = viewport.scrollLeft;
		const addedWidth = setsToAdd * slideDimensions.reduce((sum, d) => sum + d.w, 0);
		this.#track.prepend(before);
		this.#track.append(after);
		viewport.scrollLeft = scrollBefore + addedWidth;

		this.#allSlides = [...this.#track.querySelectorAll('.carousel__slide')];
		this.#clonesPerSide += setsToAdd * realCount;
		this.#cacheSlidePositions();

		debug('[carousel] expandClones', { existingSets, targetSets, setsToAdd, newClonesPerSide: this.#clonesPerSide });
	}

	/**
	 * Lighten clone images according to the configured `#cloneQuality` mode.
	 *  - `full`: keep real srcset/sizes/fetchpriority — clones look identical to real slides
	 *  - `adaptive`: strip srcset, downscale src to slide width × DPR (balanced default)
	 *  - `low`: strip srcset, use fixed width=400 (smallest memory footprint)
	 * Clones are only visible during swipe animation before reposition to real on scrollend.
	 * @param {HTMLElement} clone
	 * @param {number} slideWidthPx - Real slide's rendered width
	 */
	#processCloneImages(clone, slideWidthPx) {
		if (this.#cloneQuality === 'full') {
			for (const img of clone.querySelectorAll('img')) {
				img.removeAttribute('fetchpriority');
				img.loading = 'lazy';
				img.decoding = 'async';
			}
			return;
		}

		const cdnWidth = this.#cloneQuality === 'low' ? 400 : computeCloneImageWidth(slideWidthPx);
		for (const img of clone.querySelectorAll('img')) {
			img.removeAttribute('srcset');
			img.removeAttribute('sizes');
			img.removeAttribute('fetchpriority');
			img.loading = 'lazy';
			img.decoding = 'async';
			// A src-deferred real slide (touch hydration, see product-card.js) keeps
			// its clone deferred too: the resized URL goes to data-src and is
			// restored with the rest on first interaction.
			const deferred = !img.getAttribute('src') && img.hasAttribute('data-src');
			img.removeAttribute('data-srcset');
			const src = img.getAttribute(deferred ? 'data-src' : 'src');
			if (src && src.includes('/cdn/shop/')) {
				const resized = src.replace(/width=\d+/, `width=${cdnWidth}`);
				if (deferred) img.setAttribute('data-src', resized);
				else img.src = resized;
			}
		}
	}

	/**
	 * Create a single clone of a real slide, lightweight for swipe animation.
	 * @param {number} realIndex
	 * @param {number} width - Pre-read offsetWidth (avoids forced reflow)
	 * @param {number} height - Pre-read offsetHeight
	 * @returns {HTMLElement}
	 */
	#createClone(realIndex, width, height) {
		const clone = this.#realSlides[realIndex].cloneNode(true);
		clone.setAttribute('data-clone', '');
		clone.setAttribute('aria-hidden', 'true');
		clone.setAttribute('data-real-index', String(realIndex));
		clone.removeAttribute('id');
		stripShopifyEditorAttributes(clone);
		for (const el of clone.querySelectorAll('[tabindex], a, button, input, select, textarea')) {
			el.setAttribute('tabindex', '-1');
		}
		this.#processCloneImages(clone, width);
		clone.style.contentVisibility = 'visible';
		clone.style.containIntrinsicSize = `${width}px ${height}px`;
		return clone;
	}

	/**
	 * Expand clones in one direction to prevent hitting the scroll boundary.
	 * Called by the safety net during aggressive swipe instead of repositioning.
	 * @param {'before'|'after'} direction
	 */
	#expandClonesInDirection(direction) {
		const { viewport } = this.refs;
		const realCount = this.#realSlides.length;
		const fragment = document.createDocumentFragment();

		// Batch dimension reads before clone creation
		const dims = [];
		for (let i = 0; i < realCount; i++) {
			dims.push({ w: this.#realSlides[i].offsetWidth, h: this.#realSlides[i].offsetHeight });
		}

		for (let i = 0; i < realCount; i++) {
			fragment.appendChild(this.#createClone(i, dims[i].w, dims[i].h));
		}

		if (direction === 'before') {
			const scrollBefore = viewport.scrollLeft;
			const addedWidth = dims.reduce((sum, d) => sum + d.w, 0);
			this.#track.prepend(fragment);
			viewport.scrollLeft = scrollBefore + addedWidth;
		} else {
			this.#track.append(fragment);
		}

		this.#allSlides = [...this.#track.querySelectorAll('.carousel__slide')];
		this.#clonesPerSide += realCount;
		this.#cacheSlidePositions();
		debug('[carousel] expandClonesInDirection', { direction, newClonesPerSide: this.#clonesPerSide });
	}

	#removeClones() {
		if (!this.#track) return;
		for (const clone of this.#track.querySelectorAll('[data-clone]')) {
			clone.remove();
		}
	}

	#rebuildClones() {
		debug('[carousel] rebuildClones — recalculating for current viewport');
		this.#removeClones();

		const savedIndex = this.#currentIndex;
		this.#allSlides = [...this.#realSlides];
		this.#clonesPerSide = 0;

		this.#setupClones();

		const clampedIndex = Math.min(savedIndex, this.#realSlides.length - 1);
		this.#currentIndex = clampedIndex;
		const { viewport } = this.refs;
		if (this.#realSlides[clampedIndex]) {
			viewport.scrollLeft = this.#getRealSlidePosition(clampedIndex);
		}
	}

	#setupResize() {
		const { viewport } = this.refs;
		let firstFire = true;
		let lastWidth = viewport.clientWidth;

		const handler = () => {
			// Skip initial fire from observe() — init already positioned correctly
			if (firstFire) { firstFire = false; return; }
			if (this.#isDragging) return;

			// Only a width change moves the slides. Height-only changes — images
			// loading, the iOS address bar showing/hiding mid-swipe — must not
			// re-snap: that blocked scrollend and jumped back to the old slide
			// (on mobile PDPs, back to the first image).
			const width = viewport.clientWidth;
			if (width === lastWidth) return;
			lastWidth = width;

			this.#isResizing = true;
			clearTimeout(this.#resizeDebounceTimer);
			this.#resizeDebounceTimer = setTimeout(() => {
				// Snap to current slide after resize settles
				if (this.#realSlides[this.#currentIndex]) {
					viewport.style.scrollSnapType = 'none';
					viewport.scrollLeft = this.#realSlides[this.#currentIndex].offsetLeft;
					requestAnimationFrame(() => {
						viewport.style.scrollSnapType = '';
						this.#isResizing = false;
						this.#cacheSlidePositions();
					});
				} else {
					this.#isResizing = false;
				}
				this.#updateNavigationState();

				// Rebuild clones for loop mode (only if slides have valid dimensions)
				if (this.#isLoop && this.#realSlides[0]?.offsetWidth > 10) {
					this.#rebuildClones();
				}
				this.#updateDots();
			}, 150);
		};

		resizeCallbacks.set(viewport, handler);
		sharedResizeObserver.observe(viewport);
	}

	/**
	 * Handle Shopify section unload
	 */
	setupSectionUnload() {
		const signal = this.#abort?.signal;

		document.addEventListener(
			'shopify:section:unload',
			(event) => {
				if (event.target?.contains(this)) {
					this.destroy();
				}
			},
			{ signal }
		);
	}

	#preloadVisibleSlides() {
		const { viewport } = this.refs;
		if (!viewport) return;

		const vpLeft = viewport.scrollLeft;
		const vpWidth = this.#viewportWidth || viewport.clientWidth;
		const vpRight = vpLeft + vpWidth;
		const slides = this.#allSlides.length > 0 ? this.#allSlides : this.#realSlides;
		const positions = this.#slidePositions;

		// For product card carousels (1 slide per view), fast path
		if (positions.length > 1) {
			const slideWidth = positions[1] - positions[0];
			if (slideWidth > 0 && slideWidth >= vpWidth * 0.9) {
				const idx = Math.round(vpLeft / slideWidth);
				const slide = slides[Math.min(idx, slides.length - 1)];
				if (slide) this.#preloadImages(slide);
				return;
			}
		}

		// Multi-slide-per-view: use cached positions
		for (let i = 0; i < slides.length; i++) {
			const slideLeft = positions[i] ?? slides[i].offsetLeft;
			const nextPos = positions[i + 1];
			const slideWidth = nextPos != null ? nextPos - slideLeft : vpWidth;
			if (slideLeft + slideWidth > vpLeft && slideLeft < vpRight) {
				this.#preloadImages(slides[i]);
			}
		}
	}

	#preloadAdjacentSlides() {
		const total = this.#realSlides.length;
		const indices = [
			this.#currentIndex,
			(this.#currentIndex - 1 + total) % total,
			(this.#currentIndex + 1) % total,
		];
		for (const idx of indices) {
			this.#preloadImages(this.#realSlides[idx]);
		}
		// Also preload clones of adjacent slides
		if (this.#isLoop) {
			const domIdx = this.#getClosestSlideIndex();
			for (const offset of [-1, 0, 1]) {
				const i = domIdx + offset;
				if (i >= 0 && i < this.#allSlides.length) {
					this.#preloadImages(this.#allSlides[i]);
				}
			}
		}
	}

	#preloadImages(slide) {
		if (!slide) return;
		this.#restoreDeferredSrc(slide);
		for (const img of slide.querySelectorAll('img[loading="lazy"]')) {
			img.loading = 'eager';
		}
	}

	/**
	 * Give src-deferred images their URL back. On touch devices product-card.js
	 * hydrates galleries at idle with `src`/`srcset` parked in `data-src` /
	 * `data-srcset`: the slides and clones exist (so the first swipe lands on a
	 * live, scrollable track) but nothing is fetched — browsers load lazy images
	 * within ~1 viewport in EVERY direction, which would otherwise pull a whole
	 * 6-slide gallery plus its clones for every card near the viewport. Restored
	 * on the first real interaction (#markInteracted) or per slide as the
	 * carousel preloads it.
	 * @param {Element} root
	 */
	#restoreDeferredSrc(root) {
		for (const img of root.querySelectorAll('img[data-src]')) {
			// Hidden until it has loaded (the grid's img[data-pending] rule; the
			// preloader's capture load/error listener clears it): the slide shows
			// its placeholder background instead of a blank image with alt text.
			img.setAttribute('data-pending', '');
			const srcset = img.getAttribute('data-srcset');
			if (srcset) img.setAttribute('srcset', srcset);
			img.setAttribute('src', img.getAttribute('data-src'));
			img.removeAttribute('data-src');
			img.removeAttribute('data-srcset');
		}
	}

	/**
	 * Clean up resources
	 */
	destroy() {
		debug('[carousel] destroy called');
		this.#abort?.abort();
		this.#abort = null;
		this.#clearAutoplay();
		const { viewport } = this.refs;
		if (viewport) {
			resizeCallbacks.delete(viewport);
			sharedResizeObserver.unobserve(viewport);
		}
		clearTimeout(this.#scrollDebounceTimer);
		clearTimeout(this.#resizeDebounceTimer);

		this.#removeClones();
		viewport?.classList.remove('carousel__viewport--dragging');
		viewport?.classList.remove('carousel__viewport--ready');
		this.#isDragging = false;
		this.#dragClickPrevented = false;
		this.#dragVelocityTracker = [];

		clearTimeout(this.#repositionTimeout);
		this.#track = null;
		this.#realSlides = [];
		this.#allSlides = [];
		this.#slidePositions = [];
		this.#dotElements = [];
		this.#viewportWidth = 0;
		this.#currentIndex = 0;
		this.#lastDotIndex = -1;
		this.#clonesPerSide = 0;
		this.#autoplayStopped = false;
		this.#isRepositioning = false;
		this.initialized = false;
	}

	// ============================================================================
	// Public API for external control
	// ============================================================================

	/**
	 * Scroll to the next slide
	 * @param {boolean} [jump=false] - If true, jump instantly without animation
	 */
	scrollNext(jump = false) {
		if (jump) {
			const { viewport } = this.refs;
			const domIdx = this.#getClosestSlideIndex();

			if (this.#isLoop) {
				const targetIdx = domIdx + 1;
				if (targetIdx < this.#allSlides.length) {
					viewport.scrollTo({ left: this.#slidePositions[targetIdx], behavior: 'instant' });
				}
			} else {
				const nextReal = Math.min(this.#currentIndex + 1, this.#realSlides.length - 1);
				this.#scrollToSlide(nextReal, true);
			}
		} else {
			this.#scrollNextInternal();
		}
	}

	/**
	 * Scroll to the previous slide
	 * @param {boolean} [jump=false] - If true, jump instantly without animation
	 */
	scrollPrev(jump = false) {
		if (jump) {
			const { viewport } = this.refs;
			const domIdx = this.#getClosestSlideIndex();

			if (this.#isLoop) {
				const targetIdx = domIdx - 1;
				if (targetIdx >= 0) {
					viewport.scrollTo({ left: this.#slidePositions[targetIdx], behavior: 'instant' });
				}
			} else {
				const prevReal = Math.max(this.#currentIndex - 1, 0);
				this.#scrollToSlide(prevReal, true);
			}
		} else {
			this.#scrollPrevInternal();
		}
	}

	/**
	 * Scroll to a specific slide index
	 * @param {number} index - The slide index to scroll to
	 * @param {boolean} [jump=false] - If true, jump instantly without animation
	 */
	scrollTo(index, jump = false) {
		const clamped = Math.max(0, Math.min(index, this.#realSlides.length - 1));
		this.#scrollToSlide(clamped, jump);
	}

	/**
	 * Get the currently selected slide index
	 * @returns {number}
	 */
	selectedIndex() {
		return this.#currentIndex;
	}

	/**
	 * Get the total number of slides (real, not clones)
	 * @returns {number}
	 */
	slideCount() {
		return this.#realSlides.length;
	}

	/**
	 * Get all real slide DOM nodes (excludes clones)
	 * @returns {HTMLElement[]}
	 */
	getSlideNodes() {
		return [...this.#realSlides];
	}

	/**
	 * Stop autoplay (used by theme editor)
	 */
	stopAutoplay() {
		this.#autoplayStopped = true;
		this.#clearAutoplay();
	}

	/**
	 * Resume autoplay (used by theme editor)
	 */
	resumeAutoplay() {
		if (this.dataset.autoplay !== 'true' || this.#reducedMotion) return;
		this.#autoplayStopped = false;
		const interval = parseInt(this.dataset.autoplayInterval || this.dataset.interval, 10) || 5000;
		this.#startAutoplay(interval);
	}
}

// Register custom element
if (!customElements.get('carousel-slider')) {
	customElements.define('carousel-slider', CarouselSlider);
}
